import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Gem,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * Password rules must EXACTLY mirror the backend guardrails in
 * ``_validate_password_strength``. Keeping them here (rather than
 * fetching from the API) is fine because:
 *   • The list is short (3 rules) and stable.
 *   • The backend is still the ultimate authority — this UI just gives
 *     the user immediate feedback so they don't submit a doomed form.
 */
function passwordIssues(pw) {
  const issues = [];
  if (!pw || pw.length < 8) issues.push("At least 8 characters");
  if (!/[A-Za-z]/.test(pw || "")) issues.push("At least 1 letter");
  if (!/\d/.test(pw || "")) issues.push("At least 1 number");
  return issues;
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  // Read the token ONCE at mount into local state, then immediately strip
  // it from the visible URL so it can't leak via the address bar, browser
  // history, screenshot, or a browser extension. See the useEffect below.
  const [token, setToken] = useState(() => params.get("token") || "");

  // Three high-level phases:
  //   validating  → we're calling POST /auth/reset-token/validate
  //   invalid     → token is missing, expired, used, or superseded
  //   valid       → show the "set new password" form
  //   done        → password was set successfully; show success screen
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [emailMasked, setEmailMasked] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [done, setDone] = useState(false);

  // -----------------------------------------------------------------
  // Referrer-Policy: no-referrer for THIS page ONLY.
  //
  // Adds a <meta name="referrer" content="no-referrer"> node while
  // /reset-password is mounted, then removes it on unmount. This is
  // safer than a global change because it can't accidentally break
  // outbound clicks on other pages. Any request the browser makes
  // while this page is open (analytics beacons, image loads, an
  // errant click on an outbound link) will NOT carry the URL
  // (including the ?token= param, if it were still there) in the
  // Referer header.
  // -----------------------------------------------------------------
  useEffect(() => {
    const previous = document.querySelector('meta[name="referrer"]');
    const previousContent = previous ? previous.getAttribute("content") : null;
    let meta = previous;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "referrer");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "no-referrer");
    return () => {
      if (previousContent === null) {
        // We created it; remove it entirely.
        if (meta && meta.parentNode) meta.parentNode.removeChild(meta);
      } else {
        // Restore whatever policy the app had before we mounted.
        meta.setAttribute("content", previousContent);
      }
    };
  }, []);

  // -----------------------------------------------------------------
  // Strip ?token=... from the visible URL immediately after we've read
  // it into React state. This prevents:
  //   • Address-bar screenshots exposing the token
  //   • Browser history retaining the token
  //   • Third-party analytics scripts that read window.location.search
  //     from picking it up on route changes
  //
  // We use history.replaceState so there's no navigation event and no
  // reload — the reset flow is entirely unaffected.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!token) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("token")) {
        url.searchParams.delete("token");
        // Keep the pathname + any other query params intact.
        const clean = url.pathname + (url.search ? url.search : "") + url.hash;
        window.history.replaceState(null, "", clean);
      }
    } catch (_e) {
      /* replaceState unsupported (extremely old browsers) — non-fatal */
    }
    // Deliberately only run when ``token`` transitions from empty to set.
  }, [token]);

  useEffect(() => {
    if (!token) {
      setTokenError("It may have expired or already been used.");
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Phase 16b hardening: send the raw token in the POST BODY so it
        // never appears in the URL path (which upstream proxies /
        // ingress controllers / access logs may capture).
        const res = await api.post("/auth/reset-token/validate", { token });
        if (!cancelled) setEmailMasked(res.data?.email_masked || "");
      } catch (_err) {
        // We intentionally show a generic user-friendly message here
        // rather than the backend's ``detail`` string. This avoids
        // exposing implementation specifics (e.g. "superseded by a
        // newer request") that don't help the user take action.
        if (!cancelled) setTokenError("It may have expired or already been used.");
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const pwIssues = useMemo(() => passwordIssues(password), [password]);
  const showRules = password.length > 0 && pwIssues.length > 0;
  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");
    if (pwIssues.length) {
      setErrorMsg("Please choose a stronger password.");
      return;
    }
    if (confirm !== password) {
      setErrorMsg("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
        confirm_password: confirm,
      });
      // Server intentionally does NOT log the user back in — all their
      // sessions were revoked. Show a success screen and send them to
      // /login with the freshly-changed password.
      toast.success("Password updated");
      // Wipe the token from React state so it doesn't linger even if
      // the DOM inspector is opened after success.
      setToken("");
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Could not reset your password.";
      // If the backend now says the token is bad (e.g. someone opened
      // the same link twice), flip to the invalid state so the user
      // sees a coherent "request a new link" affordance.
      const status = err?.response?.status;
      if (status === 404 || status === 410) {
        setTokenError("It may have expired or already been used.");
      } else {
        setErrorMsg(typeof msg === "string" ? msg : "Could not reset your password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden"
      data-testid="reset-password-page"
    >
      <SEO
        title="Reset password | Postmoro"
        description="Reset the password on your Postmoro account."
        path="/reset-password"
        noindex
      />
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <header className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Postmoro</span>
        </Link>
      </header>

      <main className="relative max-w-[460px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="rounded-2xl bg-[color:var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-card)] p-6 sm:p-7">
          {validating ? (
            <div className="py-6 flex items-center justify-center text-[color:var(--text-3)] text-sm">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating reset link…
            </div>
          ) : done ? (
            /* ---------------- Success screen ---------------- */
            <div data-testid="reset-pw-success">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <h1 className="font-display text-xl font-semibold tracking-tight">
                    Password updated
                  </h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-1.5 leading-relaxed">
                    Your password has been changed successfully. For security,
                    any other devices signed in to this account have been
                    signed out.
                  </p>
                </div>
              </div>
              <Button
                asChild
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="reset-pw-back-login"
              >
                <Link to="/login">Back to login</Link>
              </Button>
            </div>
          ) : tokenError ? (
            /* ---------------- Invalid / expired token screen ---------------- */
            <div data-testid="reset-pw-invalid">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-md bg-[color:rgba(238,90,82,0.12)] text-[color:var(--danger)] flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <h1 className="font-display text-xl font-semibold">
                    This reset link is no longer valid
                  </h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-1.5 leading-relaxed">
                    {tokenError}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  asChild
                  className="flex-1 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                  data-testid="reset-pw-request-new"
                >
                  <Link to="/forgot-password">Request a new reset link</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="flex-1 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                  data-testid="reset-pw-back-login-invalid"
                >
                  <Link to="/login">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back to login
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            /* ---------------- Set-new-password form ---------------- */
            <>
              <div className="flex items-start gap-3 mb-5">
                <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <h1
                    className="font-display text-xl font-semibold tracking-tight"
                    data-testid="reset-pw-title"
                  >
                    Create a new password
                  </h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-1.5">
                    Setting a new password for{" "}
                    <span className="text-[color:var(--text-1)] font-medium">
                      {emailMasked || "your account"}
                    </span>
                    .
                  </p>
                </div>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="reset-new-password"
                    className="text-sm font-medium text-[color:var(--text-1)]"
                  >
                    New password
                  </label>
                  <div className="relative mt-1.5">
                    <Input
                      id="reset-new-password"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Min 8 characters, with a letter and a number"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="reset-pw-password"
                      className="pr-9 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      data-testid="reset-pw-toggle-visibility"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {showRules ? (
                    <ul
                      className="mt-2 text-xs text-[color:var(--text-3)] space-y-0.5"
                      data-testid="reset-pw-rules"
                    >
                      {pwIssues.map((rule) => (
                        <li key={rule}>• {rule}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-2 text-xs text-[color:var(--text-3)]">
                      Use at least 8 characters, with a letter and a number.
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="reset-confirm-password"
                    className="text-sm font-medium text-[color:var(--text-1)]"
                  >
                    Confirm new password
                  </label>
                  <Input
                    id="reset-confirm-password"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-pw-confirm"
                    className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                    required
                  />
                  {mismatch ? (
                    <div
                      className="mt-1 text-xs text-[color:var(--danger)]"
                      data-testid="reset-pw-mismatch"
                    >
                      Passwords do not match.
                    </div>
                  ) : null}
                </div>

                {errorMsg ? (
                  <div
                    className="text-xs text-[color:var(--danger)] bg-[color:rgba(238,90,82,0.06)] border border-[color:rgba(238,90,82,0.30)] rounded-md px-3 py-2"
                    data-testid="reset-pw-error"
                    role="alert"
                  >
                    {errorMsg}
                  </div>
                ) : null}

                <Button
                  type="submit"
                  disabled={loading || pwIssues.length > 0 || mismatch || !confirm}
                  data-testid="reset-pw-submit"
                  className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                >
                  {loading ? (
                    <span className="inline-flex items-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </span>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Reset password
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
